import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';

import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { REFRESH_TOKEN_NAME } from '@app/constants/constants';
import { AuthService } from '@app/services/auth.service';
import { CookieService } from '@app/services/cookie.service';

/**
 * Functional auth guard.
 * Fast path: valid non-expired token → grant access.
 * Expired token + refresh token cookie → proactive refresh.
 * Refresh fails or no refresh token → WordPress SSO cookie login.
 * All else → redirect to login.
 */
export const authGuard: CanActivateFn = (): boolean | UrlTree | Observable<boolean | UrlTree> => {
    const authService = inject(AuthService);
    const cookieService = inject(CookieService);
    const router = inject(Router);

    // Fast path: token present and not expired.
    if (!authService.isTokenExpired()) {
        return true;
    }

    const cookieLogin = (): Observable<boolean | UrlTree> => {
        const wpCookie = cookieService.get('mst_logged_id');
        if (wpCookie) {
            return authService.loginCookie(wpCookie).pipe(
                switchMap(() => of(true as const)),
                catchError(() => of(router.createUrlTree(['/login']))),
            );
        }
        authService.goToLogin();
        return of(false);
    };

    if (cookieService.get(REFRESH_TOKEN_NAME)) {
        return authService.refreshToken().pipe(
            switchMap(() => of(true as const)),
            catchError(() => cookieLogin()),
        );
    }

    return cookieLogin();
};
